import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormGroup} from '@angular/forms';
import {DinaUppgifterService} from '../../../services/dina-uppgifter.service';
import {Subscription} from 'rxjs';
import {FormService} from '../../../services/form/form.service';
import {DinaUppgifter} from '../../../shared/models/dina-uppgifter.model';
import {ModalService} from '../../../services/modal/modal.service';
import {Inkomsttyp} from '../../../shared/enums/inkomsttyp.enum';
import {RadioItem} from '../../../shared/classes/radio-item.class';
import {Utbetald} from '../../../shared/enums/utbetald.enum';
import {UtlandskSkatt} from '../../../shared/enums/utlandskskatt.enum';
import {ListItem} from '../../../shared/classes/list-item.class';
import {ErrorService} from '../../../services/error/error.service';
import {HttpErrorResponse} from '@angular/common/http';
import {ErrorType} from '../../../services/error/errorType';

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: []
})
export class FormComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  form: UntypedFormGroup;
  isEditMode = false;
  isSubmitted = false;

  viewModel: DinaUppgifter;
  lander: ListItem[] = [];

  skattAnnanValuta = '';
  annanValuta = '';

  inkomsttyper: RadioItem[] = [
    {text: 'Utdelning', value: Inkomsttyp.UTDELNING},
    {text: 'Ränta', value: Inkomsttyp.RANTA}
  ];

  utbetalning: RadioItem[] = [
    {text: 'Ja', value: Utbetald.JA},
    {text: 'Nej', value: Utbetald.NEJ}
  ];

  utlandskSkatt: RadioItem[] = [
    {text: 'Ja', value: UtlandskSkatt.JA},
    {text: 'Nej', value: UtlandskSkatt.NEJ}
  ];

  annatLand = 'Alla länder finns ännu inte med i listan. Saknas landet så kan tjänsten tyvärr inte hjälpa dig med inkomsterna därifrån.';

  isUtbetald: boolean;

  datumHelpTexts: string[] = [];

  isBeforeFirstBankDayOfNewYear: boolean;

  serverError: HttpErrorResponse = null;

  constructor(
    public dinaUppgifterService: DinaUppgifterService,
    public formService: FormService,
    private readonly cd: ChangeDetectorRef,
    private readonly modalService: ModalService,
    private readonly errorService: ErrorService
  ) {
  }

  get valutaListItems() {
    return !!this.viewModel ? this.viewModel.valutaListItems : null;
  }

  get utbetalningsdatumAr() {
    return !!this.viewModel ? this.viewModel.utbetalningsdatumAr : null;
  }

  ngOnInit() {
    this.errorService.errorSubject.subscribe((res) => {
      this.serverError = res;
    });

    this.form = this.formService.getForm(this.dinaUppgifterService.inkomstar);
    this.initSubscriptions();
    this.getViewModel();
    this.initTexts();
    this.isBeforeFirstBankDayOfNewYear = this.dinaUppgifterService.isBeforeFirstBankDayOfNewYear();
    this.onInkomsttypChanges();
  }

  onInkomsttypChanges(): void {
    this.formService.inkomsttypControl.valueChanges.subscribe(val => {
      if (val) {
        this.formService.landControl.reset();
        this.updateLander(val);
      }
    });
  }

  updateLander(inkomsttyp: Inkomsttyp) {
    if (inkomsttyp === Inkomsttyp.UTDELNING) {
      this.lander = this.viewModel.landerUtdelning;
    } else if (inkomsttyp === Inkomsttyp.RANTA) {
      this.lander = this.viewModel.landerRanta;
    }
  }

  initSubscriptions(): void {
    const editSubscription = this.dinaUppgifterService.edit$.subscribe(dittUnderlag => {
      this.resetForm();
      this.formService.setFormValue(dittUnderlag);
      this.isEditMode = true;
      this.isSubmitted = false;
      this.getInfotext();
      this.cd.detectChanges();
    });

    const removedSammanstallningItemSubscription = this.dinaUppgifterService.removedSammanstallningItem$.subscribe(id => {
      if (this.isEditMode && this.form.value.id === id) {
        this.resetForm();
      }
    });

    const cancelSubscription = this.dinaUppgifterService.resetForm$.subscribe(() => {
      this.resetForm();
    });

    this.subscriptions.push(editSubscription);
    this.subscriptions.push(removedSammanstallningItemSubscription);
    this.subscriptions.push(cancelSubscription);
  }

  initTexts(): void {
    this.datumHelpTexts = this.dinaUppgifterService.datumHelpTexts;
  }

  openModal(): void {
    this.modalService.openClearForm(() => {
      this.resetForm();
      this.dinaUppgifterService.deselectCurrentItem();
    });
  }

  onSubmit(): void {
    this.isSubmitted = true;
    this.errorService.notify(null);

    if (!this.formService.form.valid) {
      this.formService.validateAllFormFields(this.form);
      return;
    }

    if (this.isEditMode) {
      this.dinaUppgifterService.updateUnderlag(this.form.getRawValue());
    } else {
      this.dinaUppgifterService.addUnderlag(this.form.getRawValue());
    }

    this.resetForm();
    this.formService.utbetalningsdatumArControl.setValue(this.dinaUppgifterService.inkomstar);
  }

  onLandChanged(): void {
    this.formService.landChanged();

    if (!this.formService.isAnnatLand()) {
      this.getInfotext();
    }
  }

  onArChanged(): void {
    this.formService.arChanged();
  }

  onIsUtlandskSkattChanged(value: string): void {
    this.formService.isUtlandskSkattChanged(value);
    this.cd.detectChanges();
  }

  validAmount(control: AbstractControl): boolean {
    return !this.isControlEmpty(control) && +this.removeSpaceChar(control.value) > 0;
  }

  showSubmitBtn(): boolean {
    return this.formService.showSubmitBtn();
  }

  isControlEmpty(control: AbstractControl): boolean {
    return this.formService.isControlEmpty(control);
  }

  resetForm(): void {
    this.formService.resetForm(this.dinaUppgifterService.inkomstar);
    this.isSubmitted = false;
    this.isEditMode = false;
  }

  getAriaLabelButtonText(): string {
    const text1 = this.getButtonText();
    const text2 = this.isEditMode ? 'ändras' : 'läggas till';
    return `${text1}. När du klickar på knappen kommer uppgiften att ${text2} i sammanställningen.`;
  }

  getButtonText(): string {
    return this.isEditMode === true ? 'Ändra' : 'Lägg till';
  }

  isSameValutaAndSkattGreaterThanHalfInkomst(): boolean {
    return this.formService.isSameValutaAndSkattGreaterThanHalfInkomst();
  }

  isSameValutaAndSkattGreaterThanInkomst(): boolean {
    return this.formService.isSameValutaAndSkattGreaterThanInkomst();
  }

  private removeSpaceChar(str: string): string {
    return str.replace(/\s/g, '');
  }

  private getInfotext(): void {
    this.dinaUppgifterService.getInfotexter(this.formService.inkomsttypControl.value, this.formService.landControl.value,
      parseInt(this.formService.utbetalningsdatumArControl.value, 10))
      .then((viewModel: DinaUppgifter) => {
        this.annanValuta = viewModel.annanValuta;
        this.skattAnnanValuta = viewModel.skattAnnanValuta;
      })
      .catch((err: HttpErrorResponse) => {
        console.log('HTTP Error', err);
        this.formService.landControl.reset();
      });
  }

  private getViewModel(): void {
    this.dinaUppgifterService.getDittUnderlag()
      .then(
        (viewModel: DinaUppgifter) => {
          this.viewModel = viewModel;
        }, (err: HttpErrorResponse) => {
          console.log('HTTP Error', err);
        });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
  }

  isValutakursInternalError() {
    return this.serverError?.error.name === ErrorType.VALUTAKURS_INTERNAL_ERROR;
  }

  isInternalError() {
    return this.serverError?.error.error === ErrorType.INTERNAL_SERVER_ERROR
      || !this.serverError?.error.error;  // WAF kan skicka tillbaka status 500
  }

  isValutakursError() {
    return this.serverError?.error.name === ErrorType.VALUTAKURS_VALUTAKURS_ERROR;
  }
}
